import React, { useState, useEffect } from 'react'
import { Button, Card, CardBody, CardHeader, Col, Row } from 'reactstrap'
import { IActionPrice } from './index'
import { toast } from 'react-toastify'
import DeleteModal from 'Components/Common/DeleteModal'
import { useDebouncedCallback } from 'use-debounce'
import { Spinner } from 'reactstrap'
import { ISort } from 'lib/type'

interface IProds {
  fetching: boolean
  actionPriceRecords: IActionPrice[]
  handleOpenDetails: (arg1: string, arg2: string) => void
  setOpenActionPricePopup: (arg: boolean) => void
  setEditMode: (arg: boolean) => void
  handleDeleteActionPrice: (arg1: string, arg2: string) => Promise<string>
  searchActionPrice: (arg: string) => void
  sortAction: ISort
  setSortAction: (arg: ISort) => void
  setCanEdit: (arg: boolean) => void
}

const ActionPricesTable: React.FC<IProds> = ({
  actionPriceRecords,
  handleOpenDetails,
  setOpenActionPricePopup,
  setEditMode,
  fetching,
  handleDeleteActionPrice,
  searchActionPrice,
  sortAction,
  setSortAction,
  setCanEdit
}) => {
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false)
  const [actionPriceSelected, setActionPriceSelected] = useState<IActionPrice | null>(null)
  const [query, setQuery] = useState('')
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canEdit: false,
    canDelete: false,
  })

  const handleAddActionPrice = () => {
    setEditMode(false)
    setOpenActionPricePopup(true)
  }

  const handleEditActionPrice = (actionId: string, currencyId: string) => {
    setEditMode(true)
    setOpenActionPricePopup(true)
    handleOpenDetails(actionId, currencyId)
  }

  const handleDeletActionPriceRecord = async (actionPrice: IActionPrice | null) => {
    const res = handleDeleteActionPrice(
      actionPrice?.actionId as string,
      actionPrice?.currencyId as string
    )

    const message = await res
    if (message === 'SUCCESSFULL') {
      toast.success('Deleted a Action Price successfully')
    }
  }

  const handleShowModalDelete = (actionPrice: IActionPrice) => {
    setShowModalDelete(true)
    setActionPriceSelected(actionPrice)
  }

  const handleDelete = () => {
    handleDeletActionPriceRecord(actionPriceSelected)
    setShowModalDelete(false)
  }

  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    searchActionPrice(searchTerm)
  }, 1000)

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  const handleActionSort = (sortBy: string) => {
    if (sortAction.sortBy === sortBy) {
      setSortAction({
        ...sortAction,
        sortDirection: sortAction.sortDirection === 'DESC' ? 'ASC' : 'DESC'
      })
    } else {
      setSortAction({
        sortDirection: 'DESC',
        sortBy: sortBy
      })
    }
  }

  useEffect(() => {
    const userType = sessionStorage.getItem('userType')
    
    // Type 1 = Admin - full access to everything
    if (userType === '1') {
      setPermissions({
        canCreate: true,
        canEdit: true,
        canDelete: true,
      })
      setCanEdit(true)
      return
    }
    
    // Type 2 = Regular user - use role-based permissions
    const userRoles = sessionStorage.getItem('userRoles') ? JSON.parse(atob(sessionStorage.getItem('userRoles')!)) : null
    if (userRoles) {
      userRoles.forEach((role: any) => {
        // 🔑 2026-08-17: burada 'Action Prices' yaziyordu ama boyle bir yetki
        // adi HIC YOK — ne yetki tablosunda ne backend'de. Eslesme metinle
        // yapildigi icin kosul HICBIR ZAMAN tutmuyor, sayfa varsayilan
        // degerinde (hepsi false) kaliyordu: Services yetkisi verilmis bir
        // operator bile Ekle/Duzenle/Sil dugmelerini goremiyordu.
        // Bu sayfa /api/v1/action-price-records ucunu cagirir ve o uc
        // verifyAdmin.check("Services", ...) ile korunur — dogru ad budur.
        if (role.page === 'Services') {
          setPermissions({
            canCreate: role.canCreate,
            canEdit: role.canEdit,
            canDelete: role.canDelete,
          })
          setCanEdit(role.canEdit)
        }
      })
    }
  }, [setCanEdit])

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className='align-items-center d-flex justify-content-between'>
              <div className='search-box d-inline-block col-8 d-flex align-items-center'>
                <input
                  id='search-bar-action-price'
                  className='form-control search'
                  placeholder='Search Action Prices'
                  onChange={(e) => handleInputChange(e)}
                  value={query}
                />
                <i className='bx bx-search-alt search-icon'></i>
              </div>
              {
                permissions.canCreate &&
                <Button className='p-2' color='primary' onClick={(e) => handleAddActionPrice()}>
                  <div className='flex align-items-center'>
                    {fetching ? (
                      <Spinner size='sm'> Loading... </Spinner>
                    ) : (
                      <>
                        <i className='mdi mdi-plus'></i>
                        <span className='fw-semibold ms-1'>New Action Price</span>
                      </>
                    )}
                  </div>
                </Button>
              }
            </CardHeader>
            <CardBody>
              <div className='table-responsive table-card'>
                <table className='table align-middle table-nowrap table-striped-columns mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th scope='col' onClick={() => handleActionSort('actionId')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Action</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'actionId' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'actionId' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('currencyId')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Currency</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'currencyId' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'currencyId' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('priceFor1')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Price For 1</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'priceFor1' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'priceFor1' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('priceFor2')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Price For 2</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'priceFor2' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'priceFor2' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('priceFor3')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Price For 3</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'priceFor3' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'priceFor3' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' style={{ width: '150px' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {actionPriceRecords.map((actionPrice, index) => {
                      return (
                        <tr key={index}>
                          <td>{actionPrice.action.name}</td>
                          <td>{actionPrice.currency.name}</td>
                          <td>{actionPrice.priceFor1}</td>
                          <td>{actionPrice.priceFor2}</td>
                          <td>{actionPrice.priceFor3}</td>
                          <td>
                            <button
                              type='button'
                              className='btn btn-sm btn-light'
                              onClick={(e) =>
                                handleEditActionPrice(
                                  actionPrice?.actionId,
                                  actionPrice?.currencyId
                                )
                              }
                            >
                              Details
                            </button>
                            {
                              permissions.canDelete &&
                              <button
                                type='button'
                                className='btn btn-sm btn-danger ms-2'
                                onClick={(e) => handleShowModalDelete(actionPrice)}
                              >
                                Delete
                              </button>
                            }
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <DeleteModal
        show={showModalDelete}
        onDeleteClick={() => handleDelete()}
        onCloseClick={() => setShowModalDelete(false)}
      />
    </React.Fragment>
  )
}
export default ActionPricesTable
